import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { persianQuestionsData } from '../data/persianQuestions';
import { hafezLessonsData } from '../data/hafezPoems';
import blogArticles from '../data/blogArticles.json';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function highlight(text: string, query: string) {
  if (!query) return text;
  const regex = new RegExp(`(${query})`, 'gi');
  return text.split(regex).map((part, i) =>
    regex.test(part) ? <mark key={i} className="bg-yellow-200">{part}</mark> : part
  );
}

const SearchResults: React.FC = () => {
  const query = useQuery().get('q')?.trim() || '';

  // Search logic
  const results = useMemo(() => {
    // Gather all searchable items
    const lessons = [...persianQuestionsData, ...hafezLessonsData];
    const articles = blogArticles || [];
    
    if (!query) return [];
    const q = query.toLowerCase();
    const lessonResults = lessons
      .filter((l: any) =>
        l.title?.toLowerCase().includes(q) ||
        l.description?.toLowerCase().includes(q) ||
        l.phrases?.some?.((p: any) => p.persian?.includes(query) || p.english?.toLowerCase().includes(q)) ||
        l.questions?.some?.((p: any) => p.persian?.includes(query) || p.english?.toLowerCase().includes(q))
      )
      .map((l: any) => ({
        type: 'Lesson',
        title: l.title,
        snippet: l.description || '',
        link: `/lesson/${l.id || l._id}`
      }));
    const articleResults = articles
      .filter((a: any) =>
        a.title?.toLowerCase().includes(q) ||
        a.content?.toLowerCase().includes(q)
      )
      .map((a: any) => ({
        type: 'Blog Article',
        title: a.title,
        snippet: a.content?.slice(0, 120) + '...',
        link: `/blog/${a.id || a.slug}`
      }));
    // Add more sources as needed
    return [...lessonResults, ...articleResults];
  }, [query]);

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <h1 className="text-3xl font-bold mb-6">Search Results</h1>
      <form className="mb-8">
        <input
          type="text"
          value={query}
          readOnly
          className="px-4 py-2 border rounded w-full bg-gray-50 text-lg"
          placeholder="Search..."
        />
      </form>
      {query && results.length === 0 && (
        <div className="text-gray-500 text-lg">No results found for <strong>{query}</strong>.</div>
      )}
      {results.length > 0 && (
        <ul className="space-y-6">
          {results.map((result, i) => (
            <li key={i} className="bg-white rounded-lg shadow p-5 border-l-4 border-saffron">
              <div className="text-xs text-saffron font-bold mb-1">{result.type}</div>
              <Link to={result.link} className="text-xl font-semibold text-blue-700 hover:underline">
                {highlight(result.title, query)}
              </Link>
              <div className="text-gray-600 mt-2">
                {highlight(result.snippet, query)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchResults; 